import React, { useEffect, useState, useMemo } from "react";
import { FaServer, FaCogs, FaDollarSign } from 'react-icons/fa';
import {
    Grid,
    Paper,
    TextField,
} from "@mui/material";
import DebugService from "../services/DebugService";
import {
    CONST_SITE_LABEL,
    PLATFORM,
    STORAGE_KEYS,
    setScopedItem
} from "../components/common/Helper";
import SnackbarNotification, { SNACKBAR_THEME } from "../components/common/SnackbarNotification";
import '../components/common/Styles.css';
import BillingApiService from "../services/BillingApiService";
import CustomTable from "../components/common/Table.jsx";
import CheckboxLoader from "../components/common/contentloader/CheckboxLoader";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useAuth } from "../state/AuthContext";
import { useScopedLocalStorage } from "../hooks/useScopedLocalStorage";

export default function CloudCostConsole() {
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, currentDate.getDate());
    const last3Years = new Date(currentDate.getFullYear() - 3, currentDate.getMonth(), currentDate.getDate());

    const [data, setData] = useState([]);
    const [totalPriceValue, setTotalPriceValue] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState(null);
    const [snackbarStatus, setSnackbarStatus] = useState(null);
    const [selectedService, setSelectedService] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState([]);
    const [selectedMonthYear, setSelectedMonthYear] = useState(dayjs(lastMonth));
    const [cloudCostConsoleApiData, setCloudCostConsoleApiData] = useState({});

    const { selectedAccount, selectedPlatform } = useAuth();
    const { getScopedItem } = useScopedLocalStorage();

    const fetchData = () => {
        setIsLoading(true);

        if (selectedPlatform === PLATFORM.AZURE) {
            const dataRequest = {
                userAccountId: selectedAccount,
                selectedMonthYear: selectedMonthYear.format('MMMM YYYY'),
                type: 'CLOUD_COST_CONSOLE'
            };

            BillingApiService.getAzureCloudCostConsoleData(dataRequest)
                .then(res => {
                    DebugService.log(`Azure Cloud Cost Console Component: API response`, res);
                    setCloudCostConsoleApiData(res?.data ? res : {});
                    const apiData = res['data'] ?? [];
                    setData(apiData);
                    setTotalPriceValue(res['totalMonthlyCost'] ?? {});

                    if (res['error']) {
                        DebugService.error(`Azure Cloud Cost Console Component: Error caught while fetching data`, res['error']);
                    }

                    setIsLoading(false);
                })
                .catch(err => {
                    DebugService.error(`Azure Cloud Cost Console Component: Error caught while fetching data`, err);
                    setIsLoading(false);
                });

        } else {
            BillingApiService.getCloudCostConsoleData({ selectedAccount, selectedMonthYear: selectedMonthYear.format('MMMM YYYY') })
                .then(res => {
                    DebugService.log(`Cloud Cost Console Component: API response`, res);
                    setCloudCostConsoleApiData(res?.data ? res : {});
                    const apiData = res['data'] ?? [];
                    setData(apiData);
                    setTotalPriceValue(res['totalPriceValue'] ?? {});

                    if (res['error']) {
                        DebugService.error(`Cloud Cost Console Component: Error caught while fetching data`, res['error']);
                    }

                    setIsLoading(false);
                })
                .catch(err => {
                    DebugService.error(`Cloud Cost Console Component: Error caught while fetching data`, err);
                    setIsLoading(false);
                });
        }
    };

    useEffect(() => {
        document.title = `${CONST_SITE_LABEL} | Cloud Cost Console`;
    }, []);

    useEffect(() => {
        fetchData();
    }, [selectedMonthYear, selectedAccount]);

    useEffect(() => {
        setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, cloudCostConsoleApiData);
    }, [cloudCostConsoleApiData]);

    const handleServiceChange = (serviceName) => {
        if (selectedService.includes(serviceName)) {
            setSelectedService(selectedService.filter(s => s !== serviceName));
        } else {
            setSelectedService(prev => ([
                ...prev,
                serviceName
            ]));
        }
    };

    const handleRegionChange = (service, region) => {
        const regionKey = `${service['service']}-${region['region'] ?? region['location']}`;

        if (selectedRegion.includes(regionKey)) {
            setSelectedRegion(selectedRegion.filter(r => r !== regionKey));
        } else {
            setSelectedRegion(prev => ([
                ...prev,
                regionKey
            ]));
        }
    };

    // Calculate maximum costs for relative progress bar scaling
    const maxServiceCost = useMemo(() => {
        if (data.length === 0) return 1;
        return Math.max(...data.map(s => parseFloat(s['totalCost'] ?? 0)), 1);
    }, [data]);

    const zeroCostServicesCount = useMemo(() => {
        return (data ?? []).filter(d => parseFloat(d['totalCost'] ?? 0) === 0).length;
    }, [data]);

    return (
        <div style={{ padding: '24px 28px 56px', background: '#f4f6fa', minHeight: '100vh' }}>
            <style>{`
                .stats-ccc {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 16px;
                    margin-bottom: 20px;
                    margin-top: 20px;
                    width: 100%;
                }
                .stat-card-ccc {
                    border-radius: 12px;
                    padding: 12px 18px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 4px 14px rgba(16,24,40,.05);
                    border: 1px solid rgba(0,0,0,.02);
                }
                .stat-card-ccc .ico {
                    width: 42px;
                    height: 42px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    flex-shrink: 0;
                }
                .stat-card-ccc .lab {
                    font-size: 12px;
                    color: #6b7280;
                    font-weight: 600;
                }
                .stat-card-ccc .num {
                    font-size: 24px;
                    font-weight: 750;
                    letter-spacing: -.02em;
                    margin-top: 1px;
                }
                .sc-zero {
                    background: #eef4ff;
                }
                .sc-zero .ico {
                    background: #dbe8ff;
                    color: #3b82f6;
                }
                .sc-zero .num {
                    color: #1d4ed8;
                }
                .sc-total {
                    background: #e9f9f1;
                }
                .sc-total .ico {
                    background: #cdf2e1;
                    color: #10b981;
                }
                .sc-total .num {
                    color: #047857;
                }
                .sc-cost {
                    background: #fdecec;
                }
                .sc-cost .ico {
                    background: #fbd5d5;
                    color: #ef4444;
                }
                .sc-cost .num {
                    color: #1d4ed8;
                }

                .service-list-ccc {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    width: 100%;
                }
                .service-item-ccc {
                    background: #ffffff;
                    border: 1px solid #e6e9f0;
                    border-radius: 14px;
                    box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 4px 14px rgba(16,24,40,.05);
                    overflow: hidden;
                }
                .service-row-ccc {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 18px 22px;
                    cursor: pointer;
                    user-select: none;
                }
                .service-row-ccc:hover {
                    background: #f8fafc;
                }
                .exp-btn-ccc {
                    width: 26px;
                    height: 26px;
                    border-radius: 7px;
                    border: 1.5px solid #3b82f6;
                    color: #3b82f6;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    font-weight: 700;
                    flex-shrink: 0;
                    transition: transform .18s;
                }
                .service-item-ccc.open > .service-row-ccc > .exp-btn-ccc {
                    transform: rotate(45deg);
                }
                .region-item-ccc.open > .region-row-ccc > .exp-btn-ccc {
                    transform: rotate(45deg);
                }
                .service-name-ccc {
                    font-size: 15.5px;
                    font-weight: 600;
                    flex: 1;
                }
                .service-bar-ccc {
                    width: 160px;
                    height: 7px;
                    border-radius: 999px;
                    background: #edf1f6;
                    overflow: hidden;
                }
                .service-bar-ccc .fill {
                    height: 100%;
                    border-radius: 999px;
                    background: linear-gradient(90deg, #f5821f, #fbbf24);
                }
                .service-cost-ccc {
                    font-size: 16px;
                    font-weight: 750;
                    color: #1d4ed8;
                    min-width: 100px;
                    text-align: right;
                }
                .service-cost-ccc.zero {
                    color: #6b7280;
                }
                .regions-container-ccc {
                    padding: 8px 22px 18px 64px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    background: #fcfdfe;
                    border-top: 1px solid #f1f3f7;
                }
                .region-item-ccc {
                    background: #ffffff;
                    border: 1px solid #f0f2f5;
                    border-radius: 10px;
                    overflow: hidden;
                }
                .region-row-ccc {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px 16px;
                    cursor: pointer;
                    user-select: none;
                }
                .region-row-ccc:hover {
                    background: #f8fafc;
                }
                .region-name-ccc {
                    font-size: 14px;
                    font-weight: 500;
                    flex: 1;
                    color: #4b5563;
                }
                .region-cost-ccc {
                    font-size: 14px;
                    font-weight: 700;
                    color: #1d4ed8;
                    min-width: 80px;
                    text-align: right;
                }
                .region-cost-ccc.zero {
                    color: #9ca3af;
                }
                .usage-details-ccc {
                    padding: 12px 16px 16px 54px;
                    background: #ffffff;
                    border-top: 1px dashed #f0f2f5;
                }
                .empty-ccc {
                    padding: 12px 0;
                    color: #6b7280;
                    font-style: italic;
                    font-size: 13px;
                }
                @media (max-width:980px){
                    .stats-ccc { grid-template-columns: 1fr; }
                    .service-bar-ccc { display:none }
                }
            `}</style>

            <Grid container spacing={1}>
                {/* Date Filter Wrapper */}
                <Grid item xs={12} sm={4}>
                    <div style={{
                        position: 'relative', background: '#fff', border: '1px solid #e6e9f0',
                        borderRadius: 12, padding: '10px 16px'
                    }}>
                        <span style={{ fontSize: 10.5, color: '#6b7280', position: 'absolute', top: -8, left: 12, background: '#fff', padding: '0 5px', pointerEvents: 'none' }}>
                            Year and Month
                        </span>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                views={['year', 'month']}
                                format="MMMM YYYY"
                                minDate={dayjs(last3Years)}
                                maxDate={dayjs(new Date())}
                                value={selectedMonthYear}
                                onChange={(newValue) => {
                                    setSelectedMonthYear(newValue);
                                }}
                                slotProps={{
                                    textField: {
                                        variant: 'standard',
                                        InputProps: { disableUnderline: true },
                                        fullWidth: true,
                                        style: { border: 'none', outline: 'none' }
                                    }
                                }}
                                disableFuture={true}
                            />
                        </LocalizationProvider>
                    </div>
                </Grid>
                <Grid item xs={12} sm={8}></Grid>

                {/* Stats Cards */}
                <div className="stats-ccc">
                    <div className="stat-card-ccc sc-zero">
                        <div className="ico"><FaServer /></div>
                        <div>
                            <div className="num">{isLoading ? '—' : zeroCostServicesCount}</div>
                            <div className="lab">Total Zero Cost Services</div>
                        </div>
                    </div>
                    <div className="stat-card-ccc sc-total">
                        <div className="ico"><FaCogs /></div>
                        <div>
                            <div className="num">{isLoading ? '—' : (totalPriceValue['totalServices'] ?? 0)}</div>
                            <div className="lab">Total Services</div>
                        </div>
                    </div>
                    <div className="stat-card-ccc sc-cost">
                        <div className="ico"><FaDollarSign /></div>
                        <div>
                            <div className="num">${isLoading ? '—' : parseFloat(totalPriceValue['totalServicesCostValue'] ?? 0).toFixed(2)}</div>
                            <div className="lab">Total Services Cost</div>
                        </div>
                    </div>
                </div>

                <Grid item container xs={12}>
                    {isLoading && <CheckboxLoader />}

                    {!isLoading && (
                        <div className="service-list-ccc">
                            {data
                                .sort((a, b) => parseFloat(b['totalCost'] ?? 0) - parseFloat(a['totalCost'] ?? 0))
                                .map((service, serviceIndex) => {
                                    const serviceName = service['service'];
                                    const isServiceOpen = selectedService.includes(serviceName);
                                    const serviceCost = parseFloat(service['totalCost'] ?? 0);
                                    const serviceFillWidth = `${(serviceCost / maxServiceCost) * 100}%`;

                                    const regionsList = service['regions'] ?? service['locations'] ?? [];

                                    return (
                                        <div key={serviceIndex} className={`service-item-ccc ${isServiceOpen ? 'open' : ''}`}>
                                            <div className="service-row-ccc" onClick={() => handleServiceChange(serviceName)}>
                                                <span className="exp-btn-ccc">+</span>
                                                <span className="service-name-ccc">{serviceName}</span>
                                                <span className={`service-cost-ccc ${serviceCost === 0 ? 'zero' : ''}`}>
                                                    ${serviceCost.toFixed(selectedPlatform === PLATFORM.AZURE ? 4 : 2)}
                                                </span>
                                            </div>

                                            {isServiceOpen && (
                                                <div className="regions-container-ccc">
                                                    {regionsList.length === 0 ? (
                                                        <div className="empty-ccc">No regions recorded for this service.</div>
                                                    ) : (
                                                        regionsList
                                                            .sort((a, b) => parseFloat(b['cost'] ?? 0) - parseFloat(a['cost'] ?? 0))
                                                            .map((region, regionIndex) => {
                                                                const regionKey = region['region'] ?? region['location'];
                                                                const isRegionOpen = selectedRegion.includes(`${serviceName}-${regionKey}`);
                                                                const regionCost = parseFloat(region['cost'] ?? 0);

                                                                const maxRegionCost = Math.max(...regionsList.map(r => parseFloat(r['cost'] ?? 0)), 1);
                                                                const regionFillWidth = `${(regionCost / maxRegionCost) * 100}%`;

                                                                return (
                                                                    <div key={regionIndex} className={`region-item-ccc ${isRegionOpen ? 'open' : ''}`}>
                                                                        <div className="region-row-ccc" onClick={() => handleRegionChange(service, region)}>
                                                                            <span className="exp-btn-ccc">+</span>
                                                                            <span className="region-name-ccc">{regionKey}</span>
                                                                            <span className={`region-cost-ccc ${regionCost === 0 ? 'zero' : ''}`}>
                                                                                ${regionCost.toFixed(selectedPlatform === PLATFORM.AZURE ? 4 : 2)}
                                                                            </span>
                                                                        </div>

                                                                        {isRegionOpen && (
                                                                            <div className="usage-details-ccc">
                                                                                {regionCost === 0 ? (
                                                                                    <div className="empty-ccc">
                                                                                        No cost recorded for {regionKey} in {selectedMonthYear.format('MMMM YYYY')}.
                                                                                    </div>
                                                                                ) : selectedPlatform === PLATFORM.AWS ? (
                                                                                    <CustomTable
                                                                                        rows={(region['usageTypes'] ?? []).map((ut, i) => ({
                                                                                            id: `${serviceName}-${regionKey}-${i}`,
                                                                                            description: `$${ut['unitCost'] ?? 0.00} per ${ut['unitType'] || 'GB'} of ${ut['usageType']}`,
                                                                                            usageQuantity: `${ut['usage']} ${ut['unitType'] || 'GB'}`,
                                                                                            amount: `$${ut['cost']}`
                                                                                        }))}
                                                                                        columns={[
                                                                                            { key: 'description', label: 'Description', render: (row) => <span style={{ display: 'block', textAlign: 'left' }}>{row.description}</span> },
                                                                                            { key: 'usageQuantity', label: 'Usage Quantity' },
                                                                                            { key: 'amount', label: 'Amount', className: 'num' }
                                                                                        ]}
                                                                                        showToolbar={false}
                                                                                        emptyText="No usage details found."
                                                                                        subscriptionGated
                                                                                    />
                                                                                ) : (
                                                                                    <CustomTable
                                                                                        rows={(region['usageTypes'] ?? []).map((ut, i) => ({
                                                                                            ...ut,
                                                                                            id: `${serviceName}-${regionKey}-${i}`,
                                                                                            cost: `$${ut['cost']?.toFixed(2) ?? '0.00'}`,
                                                                                            usage: `${ut['usage']?.toFixed(2)}`
                                                                                        }))}
                                                                                        columns={[
                                                                                            { key: 'cost', label: 'Cost', className: 'num' },
                                                                                            { key: 'usage', label: 'Usage', className: 'num' },
                                                                                            { key: 'tier', label: 'Tier' },
                                                                                            { key: 'product', label: 'Product' },
                                                                                            { key: 'meter', label: 'Meter' },
                                                                                            { key: 'chargeType', label: 'Charge' }
                                                                                        ]}
                                                                                        showToolbar={false}
                                                                                        emptyText="No usage details found."
                                                                                        subscriptionGated
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </Grid>
            </Grid>

            {/* Snackbar Notification */}
            {snackbarStatus && <SnackbarNotification
                initialOpen={true}
                duration={5000}
                message={snackbarMessage}
                actionButtonName={'Ok'}
                theme={snackbarStatus === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
                yPosition={'top'}
                xPosition={'center'}
            />}
        </div>
    );
}
